<script setup>
import { useDialogPluginComponent, QChip } from 'quasar'
import { useQuasar } from 'quasar'
import { computed, reactive, ref, provide } from 'vue'
import apiAddress from '../../../api/address.api'
import { useI18n } from "vue-i18n"
import useAddress from '../../../store/address.store'
import { GoogleMap, Circle, Polygon, Polyline } from 'vue3-google-map'
const { t } = useI18n()

const $q = useQuasar()
const { dialogRef, onDialogCancel } = useDialogPluginComponent()
const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY
const CLOUD_MAP_ID = computed(() =>
  $q.dark.isActive
    ? import.meta.env.VITE_CLOUD_MAP_ID_DARK
    : import.meta.env.VITE_CLOUD_MAP_ID_LIGHT
)

const loading = ref(false)

const data = reactive({
  name: '',
  city: '',
  district: '',
  subDistrict: '',
  street: '',
  building: '',
  locationPolygon: [],
  admins: [],
  devices: [],
  prices: {
    entrance: 0,
    garage: 0,
    barrier: 0,
  },
})

const {
  addressList,
  getAddressList,
  page,
  lastPage
} = useAddress()

const submit = async () => {
  try {
    loading.value = true

    await apiAddress.create({
      name: data.name,
      city: data.city,
      district: data.district,
      subDistrict: data.subDistrict,
      street: data.street,
      building: data.building,
      locationPolygon: data.locationPolygon.at(0),
      admins: data.admins,
      prices: {
        entrance: Number(data.prices.entrance) || 0,
        garage: Number(data.prices.garage) || 0,
        barrier: Number(data.prices.barrier) || 0,
      },
      devices: data.devices.map((device) => ({
        ...device,
        deviceCameraId: device.deviceCameraId?.trim() || '',
      })),
    })

    $q.notify({ message: t('addresses.create_success'), type: 'positive' })
    onDialogCancel()
  } catch (error) {
    $q.notify({ message: error?.data?.error, type: 'negative' })
  } finally {
    getAddressList()
    loading.value = false
  }
}

const newAdmin = ref('')

const addAdmin = () => {
  if (newAdmin.value.trim()) {
    data.admins.push(newAdmin.value.trim())
    newAdmin.value = ''
  }
}

const removeAdmin = (index) => {
  data.admins.splice(index, 1)
}

const removeDevice = (index) => {
  deviceMarkers.value.splice(index, 1)
  data.devices.splice(index, 1)
}

const editDevice = (index) => {
  const device = data.devices[index]
  const marker = deviceMarkers.value[index]
  if (!device || !marker) return

  editingDeviceIndex.value = index
  pendingDeviceLocation.value = [marker.lng, marker.lat]
  pendingDevice.name = device.name
  pendingDevice.deviceId = device.deviceId
  pendingDevice.deviceNumber = device.deviceNumber
  pendingDevice.deviceCameraId = device.deviceCameraId || ''
  pendingDevice.type = device.type || 'TUYA'
  deviceDialogOpen.value = true
}

const location = ref([106.91753580953763, 47.916671069424204])
provide('coordinates', location)
let currentLocation = [...location.value]

const mapCenter = ref({
  lat: location.value[1],
  lng: location.value[0],
})

const polygonPath = ref([])
const overlayVersion = ref(0)
const polygonFinished = ref(false)
const deviceMarkers = ref([])

const polygonOptions = {
  fillColor: '#467aff',
  fillOpacity: 0.6,
  strokeColor: '#467aff',
  strokeOpacity: 1,
  strokeWeight: 2,
  clickable: false,
}

const polylineOptions = {
  strokeColor: '#467aff',
  strokeOpacity: 1,
  strokeWeight: 2,
  clickable: false,
}

const renderedPolylineOptions = computed(() => ({
  ...polylineOptions,
  path: polygonPath.value,
}))

const renderedPolygonOptions = computed(() => ({
  ...polygonOptions,
  paths: polygonPath.value,
}))

const pointCircleOptions = (point) => ({
  center: point,
  radius: 2,
  strokeColor: '#ffffff',
  strokeOpacity: 1,
  strokeWeight: 2,
  fillColor: '#467aff',
  fillOpacity: 0.95,
  clickable: true,
  draggable: true,
})

const updatePointFromCircleDrag = (index, event) => {
  if (!event?.latLng || !polygonPath.value[index]) return

  const lng = event.latLng.lng()
  const lat = event.latLng.lat()

  polygonPath.value[index] = { lat, lng }
  location.value = [lng, lat]
  currentLocation = [...location.value]
  syncLocationPolygon()
  overlayVersion.value += 1
}

const syncLocationPolygon = () => {
  data.locationPolygon =
    polygonPath.value.length >= 3
      ? [polygonPath.value.map((point) => [point.lng, point.lat])]
      : []
}

const undoPolygonPoint = () => {
  polygonPath.value.pop()
  syncLocationPolygon()
  overlayVersion.value += 1
}

const clearPolygon = () => {
  polygonPath.value = []
  polygonFinished.value = false
  deviceMarkers.value = []
  data.devices = []
  syncLocationPolygon()
  overlayVersion.value += 1
}

const finishPolygonDrawing = () => {
  if (polygonPath.value.length < 3) {
    $q.notify({ message: t('addresses.polygon_min_points'), type: 'negative' })
    return
  }

  polygonFinished.value = true
}

const deviceDialogOpen = ref(false)
const pendingDeviceLocation = ref(null)
const editingDeviceIndex = ref(null)
const deviceTypeOptions = computed(() => [
  { label: t('devices.types.ESP32'), value: 'ESP32' },
  { label: t('devices.types.TUYA'), value: 'TUYA' },
  { label: t('devices.types.TUYA-GARAGE-DOOR'), value: 'TUYA-GARAGE-DOOR' },
  { label: t('devices.types.TUYA-GARAGE-DOOR-WB-2G'), value: 'TUYA-GARAGE-DOOR-WB-2G' },
  { label: t('devices.types.PARKINGMETRIX-BARRIER'), value: 'PARKINGMETRIX-BARRIER' },
])

const pendingDevice = reactive({
  name: '',
  deviceId: '',
  deviceNumber: '',
  deviceCameraId: '',
  type: 'TUYA',
})

const resetPendingDevice = () => {
  pendingDevice.name = ''
  pendingDevice.deviceId = ''
  pendingDevice.deviceNumber = ''
  pendingDevice.deviceCameraId = ''
  pendingDevice.type = 'TUYA'
  pendingDeviceLocation.value = null
  editingDeviceIndex.value = null
}

const openDeviceDialog = (lat, lng) => {
  pendingDeviceLocation.value = [lng, lat]
  pendingDevice.name = ''
  pendingDevice.deviceId = ''
  pendingDevice.deviceNumber = ''
  pendingDevice.deviceCameraId = ''
  deviceDialogOpen.value = true
}

const confirmAddDevice = () => {
  if (
    !pendingDevice.name.trim() ||
    !pendingDevice.deviceId.trim() ||
    !pendingDevice.deviceNumber.trim() ||
    !pendingDeviceLocation.value
  ) {
    $q.notify({ message: t('addresses.fill_device_fields'), type: 'negative' })
    return
  }

  const [lng, lat] = pendingDeviceLocation.value
  const nextDevice = {
    name: pendingDevice.name.trim(),
    deviceId: pendingDevice.deviceId.trim(),
    deviceNumber: pendingDevice.deviceNumber.trim(),
    deviceCameraId: pendingDevice.deviceCameraId.trim(),
    type: pendingDevice.type,
    location: [lng, lat],
  }

  if (editingDeviceIndex.value !== null) {
    const index = editingDeviceIndex.value
    data.devices[index] = nextDevice
    deviceMarkers.value[index] = { lat, lng }
  } else {
    deviceMarkers.value.push({ lat, lng })
    data.devices.push(nextDevice)
  }

  deviceDialogOpen.value = false
  resetPendingDevice()
}

const cancelAddDevice = () => {
  deviceDialogOpen.value = false
  resetPendingDevice()
}

const deviceCircleOptions = (point) => ({
  center: point,
  radius: 2.5,
  strokeColor: '#00c853',
  strokeOpacity: 1,
  strokeWeight: 2,
  fillColor: '#00c853',
  fillOpacity: 0.95,
  clickable: true,
  draggable: true,
})

const updateDevicePointFromCircleDrag = (index, event) => {
  if (!event?.latLng || !deviceMarkers.value[index] || !data.devices[index]) return

  const lng = event.latLng.lng()
  const lat = event.latLng.lat()
  deviceMarkers.value[index] = { lat, lng }
  data.devices[index].location = [lng, lat]
}

const googleMapOptions = {
  clickableIcons: false,
}

const updateLocationFromMap = (event) => {
  if (!event?.latLng) return

  const lng = event.latLng.lng()
  const lat = event.latLng.lat()

  location.value = [lng, lat]
  currentLocation = [...location.value]

  if (polygonFinished.value) {
    openDeviceDialog(lat, lng)
    return
  }

  polygonPath.value.push({ lat, lng })
  syncLocationPolygon()
  overlayVersion.value += 1
}

</script>

<template lang='pug'>
.device-create
  q-dialog(ref='dialogRef' persistent)
    q-card.overflow-hidden(style='width: 95%; height: 100%; max-width: 95vw; display: flex; flex-direction: column;')
      q-form(@submit='submit' style='height: 100%; display: flex; flex-direction: column;')
        q-card-section
          .text-h6 {{ t('addresses.createTitle') }}
        q-separator
        q-card-section(style='flex: 1; min-height: 40vh; overflow: hidden; display: flex; flex-direction: column;')
          .row.q-ma-xs.q-mt-sm(style='flex: 1; min-height: 0;')
            div(style="width: 25%; height: 100%; min-height: 0; display: flex; flex-direction: column; padding-right: 16px;")
              .address-fields-scroll
                q-input.q-ma-xs.q-mt-sm(
                  stack-label
                  outlined
                  v-model='data.name'
                  :label="t('addresses.name')"
                  no-error-icon
                  hide-bottom-space
                  hide-hint
                )
                q-input.q-ma-xs.q-mt-sm(
                  stack-label
                  outlined
                  v-model='data.city'
                  :label="t('addresses.city')"
                  no-error-icon
                  hide-bottom-space
                  hide-hint
                )
                q-input.q-ma-xs.q-mt-sm(
                  stack-label
                  outlined
                  v-model='data.district'
                  :label="t('addresses.district')"
                  no-error-icon
                  hide-bottom-space
                  hide-hint
                )
                q-input.q-ma-xs.q-mt-sm(
                  stack-label
                  outlined
                  v-model='data.subdistrict'
                  :label="t('addresses.subdistrict')"
                  no-error-icon
                  hide-bottom-space
                  hide-hint
                )
                q-input.q-ma-xs.q-mt-sm(
                  stack-label
                  outlined
                  v-model='data.street'
                  :label="t('addresses.street')"
                  no-error-icon
                  hide-bottom-space
                  hide-hint
                )
                q-input.q-ma-xs.q-mt-sm(
                  stack-label
                  outlined
                  v-model='data.building'
                  :label="t('addresses.building')"
                  no-error-icon
                  hide-bottom-space
                  hide-hint
                )
                q-input.q-ma-xs.q-mt-sm(
                  stack-label
                  outlined
                  v-model='newAdmin'
                  :label="t('addresses.admins')"
                  hide-bottom-space
                  hide-hint
                  use-chips
                  multiple
                  separator=","
                  @keydown.enter.prevent
                )
                  q-btn(
                    flat
                    unelevated
                    color="primary"
                    @click="addAdmin"
                    :label="t('addresses.add')"
                  )
                q-chip(
                  v-for="(admin, index) in data.admins"
                  :key="index"
                  removable
                  @remove="removeAdmin(index)"
                ) {{ admin }}
                div(style="height: 20px")
                .text-subtitle2.q-ma-xs.q-mt-sm {{ t('addresses.price') }}
                q-input.q-ma-xs.q-mt-sm(
                  stack-label
                  outlined
                  type="number"
                  v-model.number='data.prices.entrance'
                  :label="t('addresses.price_entrance') + ' (₮)'"
                  no-error-icon
                  hide-bottom-space
                  hide-hint
                  :min="0"
                )
                q-input.q-ma-xs.q-mt-sm(
                  stack-label
                  outlined
                  type="number"
                  v-model.number='data.prices.garage'
                  :label="t('addresses.price_garage') + ' (₮)'"
                  no-error-icon
                  hide-bottom-space
                  hide-hint
                  :min="0"
                )
                q-input.q-ma-xs.q-mt-sm(
                  stack-label
                  outlined
                  type="number"
                  v-model.number='data.prices.barrier'
                  :label="t('addresses.price_barrier') + ' (₮)'"
                  no-error-icon
                  hide-bottom-space
                  hide-hint
                  :min="0"
                )
            div(style="width: 25%; height: 100%; min-height: 0; display: flex; flex-direction: column; padding: 0 16px;")
              .text-h6.q-mt-sm {{ t('addresses.addDevices') }}
              .text-caption.text-grey-7.q-mb-sm {{ t('addresses.click_map_hint') }}
              .device-list-scroll
                .text-caption.text-grey-6(v-if='data.devices.length === 0') {{ t('addresses.no_devices') }}
                div(v-for="(device, index) in data.devices" :key="`device-info-${index}`")
                  .row.items-center.justify-between.q-mt-sm
                    .text-subtitle2 {{ t('addresses.device_number', { number: index + 1 }) }}
                    .row.items-center.q-gutter-xs
                      q-btn(flat dense icon="edit" color="primary" @click="editDevice(index)")
                      q-btn(flat dense round icon="close" color="negative" @click="removeDevice(index)")
                  .text-body2.q-mt-xs {{ t('devices.name') }}: {{ device.name }}
                  .text-body2 {{ t('devices.deviceId') }}: {{ device.deviceId }}
                  .text-body2 {{ t('devices.deviceNumber') }}: {{ device.deviceNumber }}
                  .text-body2 {{ t('devices.type') }}: {{ t('devices.types.' + device.type) }}
                  .text-body2(v-if='device.deviceCameraId') {{ t('addresses.camera_id_optional') }}: {{ device.deviceCameraId }}
                  q-separator.q-my-sm
            div(style="width: 50%; padding-left: 20px; margin-top: -32px;")
                .row.items-center.q-gutter-sm.q-ma-xs.q-mt-md
                  q-btn(
                    flat
                    dense
                    color="primary"
                    icon="undo"
                    :disable="polygonPath.length === 0"
                    @click="undoPolygonPoint"
                    :label="t('addresses.polygon_undo')"
                  )
                  q-btn(
                    flat
                    dense
                    color="negative"
                    icon="delete"
                    :disable="polygonPath.length === 0"
                    @click="clearPolygon"
                    :label="t('actions.clear')"
                  )
                  q-btn(
                    flat
                    dense
                    color="positive"
                    icon="check"
                    :disable="polygonPath.length < 3 || polygonFinished"
                    @click="finishPolygonDrawing"
                    :label="t('addresses.polygon_finish')"
                  )
                  q-chip(color="primary" text-color="white") {{ t('addresses.polygon_points', { count: polygonPath.length }) }}
                  q-chip(v-if="polygonFinished" color="positive" text-color="white") {{ t('addresses.polygon_locked') }}
                .map-container.q-ma-xs.q-mt-md.rounded-borders.overflow-hidden.shadow-1
                component.map-view(
                  :is="GoogleMap"
                  :api-key="GOOGLE_MAPS_API_KEY"
                  :mapId="CLOUD_MAP_ID"
                  :options="googleMapOptions"
                  :center="mapCenter"
                  :zoom="14"
                  :fullscreenControl="false"
                  :streetViewControl="false"
                  :mapTypeControl="false"
                  gestureHandling="greedy"
                  @click="updateLocationFromMap"
                )
                  component(
                    :is="Circle"
                    v-for="(point, index) in polygonPath"
                    :key="`polygon-point-${index}`"
                    :options="pointCircleOptions(point)"
                    @dragend="(event) => updatePointFromCircleDrag(index, event)"
                  )
                  component(
                    :is="Polyline"
                    v-if="polygonPath.length >= 2"
                    :key="`polyline-${polygonPath.length}-${overlayVersion}`"
                    :options="renderedPolylineOptions"
                  )
                  component(
                    :is="Polygon"
                    v-if="polygonPath.length >= 3"
                    :key="`polygon-${polygonPath.length}-${overlayVersion}`"
                    :options="renderedPolygonOptions"
                  )
                  component(
                    :is="Circle"
                    v-for="(point, index) in deviceMarkers"
                    :key="`device-marker-${index}`"
                    :options="deviceCircleOptions(point)"
                    @dragend="(event) => updateDevicePointFromCircleDrag(index, event)"
                  )
          q-separator
          q-card-actions(align='right' style="z-index: 1;")
            q-btn(
              flat
              unelevated
              color='grey'
              @click='onDialogCancel'
              :label="t('actions.cancel')")
            q-btn(
              flat
              type='submit'
              unelevated
              color='primary'
              :label="t('actions.create')")
      q-inner-loading(:showing='loading')

  q-dialog(v-model='deviceDialogOpen' persistent)
    q-card(style='width: 480px; max-width: 90vw;')
      q-card-section
          .text-h6 {{ editingDeviceIndex !== null ? t('addresses.device_dialog_edit_title') : t('addresses.device_dialog_add_title') }}
      q-separator
      q-card-section
        q-input.q-mb-sm(
          outlined
          stack-label
          v-model='pendingDevice.name'
          :label="t('devices.name')"
          hide-bottom-space
          hide-hint
        )
        q-input.q-mb-sm(
          outlined
          stack-label
          v-model='pendingDevice.deviceId'
          :label="t('devices.deviceId')"
          hide-bottom-space
          hide-hint
        )
        q-input.q-mb-sm(
          outlined
          stack-label
          v-model='pendingDevice.deviceNumber'
          :label="t('devices.deviceNumber')"
          hide-bottom-space
          hide-hint
        )
        q-select.q-mb-sm(
          outlined
          stack-label
          v-model='pendingDevice.type'
          :options='deviceTypeOptions'
          :label="t('devices.type')"
          emit-value
          map-options
          hide-bottom-space
          hide-hint
        )
        q-input.q-mb-sm(
          outlined
          stack-label
          v-model='pendingDevice.deviceCameraId'
          :label="t('addresses.camera_id_optional')"
          hide-bottom-space
          hide-hint
        )
      q-separator
      q-card-actions(align='right')
        q-btn(flat color='grey' :label="t('actions.cancel')" @click='cancelAddDevice')
        q-btn(flat color='primary' :label="editingDeviceIndex !== null ? t('actions.save') : t('actions.add')" @click='confirmAddDevice')
</template>

<style scoped>
.address-fields-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.device-list-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.map-container {
  border: 1px solid var(--q-card-border-color);
}

.map-view {
  width: 100%;
  height: 90%;
  border: 1px solid var(--q-card-border-color);
  margin-top: -16px;
}
</style>
